/* GovSchemeCard — v20 government scheme / support route card (status-coloured). */
import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, CalendarCheck, IndianRupee } from 'lucide-react';
import { STATUS, StatusChip, SourceLink, LazyImg, Field, Chips, orSource, CHECK_SOURCE } from './ownpathUi';

export default function GovSchemeCard({ s, testidPrefix = 'v20-scheme' }) {
  const [open, setOpen] = useState(false);
  const vis = STATUS[s.statusColour] || STATUS.grey;
  return (
    <article className="rounded-2xl border-2 border-gray-200 bg-white overflow-hidden hover:shadow-md transition-shadow" data-testid={`${testidPrefix}-${s.id}`}>
      <div className={`h-1.5 w-full bg-gradient-to-r ${vis.bar}`} />
      <div className="flex flex-col sm:flex-row">
        <LazyImg image={s.image} className="w-full sm:w-44 h-36 sm:h-auto shrink-0" testid={`${testidPrefix}-${s.id}-img`} />
        <div className="flex-1 p-4 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            <StatusChip colour={s.statusColour} label={s.statusLabel} testid={`${testidPrefix}-${s.id}-status`} />
            <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{s.state}</span>
            {s.type && <span className="text-[10px] font-semibold bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{s.type}</span>}
          </div>
          <h4 className="text-base md:text-lg font-extrabold text-gray-900 leading-snug">{s.title}</h4>
          <p className="text-sm text-gray-700 mt-1">{orSource(s.purpose)}</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <Field label="Who it is for"><Chips items={s.whoItIsFor?.slice(0, 4)} tone="bg-emerald-50 text-emerald-900 border-emerald-200" /></Field>
            <Field label="Support type"><Chips items={s.supportTypes?.slice(0, 4)} tone="bg-teal-50 text-teal-900 border-teal-200" /></Field>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[11px] text-gray-600">
            <span className="inline-flex items-center gap-1"><CalendarCheck size={11} /> Last verified: <strong>{orSource(s.lastVerified)}</strong></span>
            <span>Current status: <strong>{orSource(s.status)}</strong></span>
          </div>
          {s.warning && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2" data-testid={`${testidPrefix}-${s.id}-warning`}>
              <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900 leading-snug"><strong>Eligibility warning:</strong> {s.warning}</p>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <SourceLink href={s.primaryUrl} label={CHECK_SOURCE} testid={`${testidPrefix}-${s.id}-cta`} />
            <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open}
              className="px-3 py-1.5 rounded-full bg-white border border-gray-300 text-gray-800 text-xs font-bold hover:bg-gray-50 inline-flex items-center gap-1"
              data-testid={`${testidPrefix}-${s.id}-toggle`}>
              {open ? <>Less <ChevronUp size={12} /></> : <>Details <ChevronDown size={12} /></>}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="border-t border-gray-100 p-4 grid md:grid-cols-2 gap-4 bg-gray-50/60" data-testid={`${testidPrefix}-${s.id}-details`}>
          {s.publicationNote && <p className="md:col-span-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-xl px-3 py-2">{s.publicationNote}</p>}
          <Field label="Key figures — planning estimate, recheck current guideline">
            {s.keyFigures?.length ? (
              <ul className="space-y-1">{s.keyFigures.map((k, i) => <li key={i} className="flex gap-1.5 text-xs"><IndianRupee size={12} className="shrink-0 mt-0.5 text-gray-500" />{k}</li>)}</ul>
            ) : <span className="text-xs italic text-gray-500">{CHECK_SOURCE}</span>}
          </Field>
          <Field label="Documents usually asked"><Chips items={s.documents} tone="bg-amber-50 text-amber-900 border-amber-200" /></Field>
          <Field label="Application route (official steps)">
            {s.applyProcess?.length ? (
              <ol className="space-y-1 text-xs list-decimal pl-4">{s.applyProcess.map((p, i) => <li key={i}>{p}</li>)}</ol>
            ) : <span className="text-xs italic text-gray-500">{CHECK_SOURCE}</span>}
          </Field>
          <Field label="Official sources">
            <div className="flex flex-col gap-1.5">
              {s.officialLinks?.length ? s.officialLinks.map((lk, i) => (
                <a key={i} href={lk.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-blue-700 hover:underline break-all" data-testid={`${testidPrefix}-${s.id}-link-${i}`}>{lk.label || lk.url}</a>
              )) : <span className="text-xs italic text-gray-500">{CHECK_SOURCE}</span>}
            </div>
            <p className="text-[11px] text-gray-500 mt-2">Verification: {orSource(s.verification?.status)}{s.verification?.confidence ? ` · confidence ${s.verification.confidence}` : ''}</p>
            {s.verification?.limitation && <p className="text-[11px] text-gray-500 italic mt-1">{s.verification.limitation}</p>}
          </Field>
        </div>
      )}
    </article>
  );
}
